/**
 * Travel Carbon Estimator
 * Copyright (C) 2026  Manuel Leduc
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import type { Session, User } from '@supabase/supabase-js'
import { computed, ref, type Ref, shallowRef } from 'vue'
import { getSupabase, isSupabaseConfigured } from '@/lib/supabase'

type AuthState = {
  session: Ref<Session | null>
  user: Ref<User | null>
  isSignedIn: Ref<boolean>
  isConfigured: Ref<boolean>
  authReady: Ref<boolean>
  signInWithOtp: (email: string) => Promise<{ error: Error | null }>
  signOut: () => Promise<{ error: Error | null }>
}

let state: AuthState | null = null

function buildRedirectUrl(): string | undefined {
  if (typeof window === 'undefined') return undefined
  return `${window.location.origin}/auth/callback`
}

function init(): AuthState {
  const session = shallowRef<Session | null>(null)
  const user = computed(() => session.value?.user ?? null)
  const isSignedIn = computed(() => session.value !== null)
  const isConfigured = ref(isSupabaseConfigured())
  const authReady = ref(!isConfigured.value)

  if (isConfigured.value) {
    const supabase = getSupabase()
    supabase.auth.getSession().then(({ data }) => {
      session.value = data.session
      authReady.value = true
    })
    supabase.auth.onAuthStateChange((_event, newSession) => {
      session.value = newSession
    })
  }

  async function signInWithOtp(email: string): Promise<{ error: Error | null }> {
    if (!isConfigured.value) {
      return { error: new Error('Supabase is not configured') }
    }
    const supabase = getSupabase()
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: buildRedirectUrl() },
    })
    return { error }
  }

  async function signOut(): Promise<{ error: Error | null }> {
    if (!isConfigured.value) {
      return { error: null }
    }
    const supabase = getSupabase()
    const { error } = await supabase.auth.signOut()
    return { error }
  }

  return { session, user, isSignedIn, isConfigured, authReady, signInWithOtp, signOut }
}

export function useAuth(): AuthState {
  if (!state) state = init()
  return state
}
